/**
 * Słowniczek metryk dla nietechnicznych twórców.
 * Jedno źródło etykiet i wyjaśnień we wszystkich zakładkach Sprzedaż.
 */

export type MetricTipId =
  | "cart_sessions_ours"
  | "purchases_easycart"
  | "purchases_on_path"
  | "purchase_coverage"
  | "revenue_easycart"
  | "aov"
  | "customers"
  | "free_orders"
  | "paid_orders"
  | "funnel_view"
  | "funnel_buy_click"
  | "funnel_cart"
  | "funnel_purchase_ec"
  | "funnel_entry"
  | "funnel_content"
  | "funnel_cta"
  | "funnel_checkout"
  | "funnel_purchase_path"
  | "small_sample"
  | "product_visits"
  | "landings_to_cart"
  | "cvr_to_cart"
  | "cvr_to_purchase"
  | "median_scroll"
  | "abandoned_carts"
  | "cart_to_purchase"
  | "visits"
  | "path_order"
  | "section_reach"
  | "section_cta"
  | "section_buyers"
  | "impulse"
  | "ga4_vs_real"
  | "via_filter"
  | "yt_funnel_overview"
  | "yt_funnel_clicks"
  | "yt_funnel_sessions"
  | "traffic_filter"
  | "visitor_filter"
  | "version_filter"
  | "kpi_today"
  | "kpi_period"
  | "easycart_shopping_sessions_note"
  | "biggest_drop"
  | "daily_chart"
  | "hourly_chart"
  | "acquisition_channels"
  | "paid_entries"
  | "other_entries"
  | "shopping_session_list"
  | "session_timeline"
  | "session_attribution"
  | "reel_reach"
  | "manychat_keywords"
  | "content_purchase_hard"
  | "content_purchase_proximate"
  | "content_purchase_unlinked"
  | "funnel_views"
  | "funnel_entries"
  | "funnel_carts"
  | "funnel_purchases"
  | "funnel_wow"
  | "funnel_mom"
  | "content_top_hard"
  | "content_top_proximate"
  | "content_top_corr"
  | "intel_score"
  | "intel_triggers"
  | "intel_life_flash"
  | "intel_life_medium"
  | "intel_life_evergreen"
  | "comments_total"
  | "comments_7d"
  | "comments_cta"
  | "comments_historia"
  | "comments_pytanie"
  | "comments_action"
  | "pub_section_reach"
  | "pub_section_funnel"
  | "pub_ig_reach"
  | "pub_ig_saved"
  | "pub_ig_shares"
  | "pub_ig_comments"
  | "pub_ig_save_rate"
  | "pub_fb_reach"
  | "pub_fb_impressions"
  | "pub_fb_comments"
  | "pub_delta_1d"
  | "pub_sessions"
  | "pub_checkouts"
  | "pub_purchases"
  | "pub_cvr_checkout"
  | "pub_cvr_purchase"
  | "pub_score"
  | "pub_via"
  | "pub_kpi_publications"
  | "pub_kpi_reach"
  | "pub_kpi_funnel"
  | "pub_kpi_cvr"
  | "funnel_tofu"
  | "funnel_mofu"
  | "funnel_bofu";

export type MetricTip = {
  label: string;
  short: string;
  long: string;
};

export const METRICS_GLOSSARY: Record<MetricTipId, MetricTip> = {
  cart_sessions_ours: {
    label: "Wejścia do koszyka (nasz licznik)",
    short: "Ile razy ktoś otworzył stronę koszyka.",
    long:
      "Ile razy ktoś otworzył stronę koszyka. Liczymy to własnym skryptem na stronie. " +
      "To NIE jest to samo co „Sesje zakupowe” w panelu EasyCart: EasyCart liczy po swojemu " +
      "i nie udostępnia nam tej liczby. Różnica kilku wizyt jest normalna.",
  },
  purchases_easycart: {
    label: "Zakupy (EasyCart)",
    short: "Opłacone zamówienia z EasyCart.",
    long:
      "Opłacone zamówienia z EasyCart. To jest prawdziwa sprzedaż i na tej liczbie polegamy. " +
      "Anulowane i oczekujące na zapłatę nie wliczają się.",
  },
  purchases_on_path: {
    label: "Zakupy widoczne na ścieżce",
    short: "Zakupy, które udało się połączyć z wizytą na stronie.",
    long:
      "Ile zakupów nasz licznik potrafił połączyć z konkretną wizytą na stronie. " +
      "Gdy jest mniej niż zakupów w EasyCart, część osób weszła do koszyka z pominięciem naszej strony " +
      "(np. prosto z linka) albo licznik ich nie zobaczył. Sprzedaż i tak jest policzona w EasyCart.",
  },
  purchase_coverage: {
    label: "Ile zakupów widzimy na ścieżce",
    short: "Zakupy na ścieżce ÷ zakupy EasyCart.",
    long:
      "Udział zakupów EasyCart, które nasz licznik połączył z wizytą. " +
      "100% oznacza pełne pokrycie. Niżej: część zakupów bez śladu na stronie (to normalne).",
  },
  revenue_easycart: {
    label: "Przychód EasyCart",
    short: "Suma pieniędzy z opłaconych zamówień.",
    long:
      "Suma kwot z opłaconych zamówień EasyCart w wybranym okresie. " +
      "Anulowane i nieopłacone nie wchodzą. Dzień liczony w czasie polskim, jak w panelu EasyCart.",
  },
  aov: {
    label: "Średnia wartość zamówienia",
    short: "Przychód ÷ liczba opłaconych zamówień.",
    long: "Średnia kwota jednego opłaconego zamówienia w okresie (w żargonie: AOV).",
  },
  customers: {
    label: "Klienci",
    short: "Unikalne adresy e-mail kupujących.",
    long: "Ile różnych osób (unikalnych e-maili) kupiło w okresie. Jedna osoba z dwoma zamówieniami = 1 klient.",
  },
  free_orders: {
    label: "Darmowe produkty",
    short: "Zamówienia za 0 zł.",
    long: "Zamówienia za 0 zł (np. darmowy rozdział). Nie wliczają się do przychodu.",
  },
  paid_orders: {
    label: "Udane transakcje",
    short: "Zamówienia z zapłaconą kwotą większą od zera.",
    long: "Liczba opłaconych zamówień (kwota > 0). Anulowane nie wchodzą.",
  },
  funnel_view: {
    label: "Widok produktu",
    short: "Wizyty, w których ktoś zobaczył produkt.",
    long: "Ile unikalnych wizyt zobaczyło ten produkt (np. kartę produktu na landingu).",
  },
  funnel_buy_click: {
    label: "Klik „Kupuję”",
    short: "Kliknięcie przycisku kupna / przejście do checkoutu.",
    long: "Ile wizyt kliknęło „Kupuję” albo weszło w ścieżkę zakupu tego produktu.",
  },
  funnel_cart: {
    label: "Koszyk",
    short: "Otwarcie strony koszyka EasyCart.",
    long: "Ile wizyt otworzyło koszyk tego produktu (nasz licznik na stronie koszyka).",
  },
  funnel_purchase_ec: {
    label: "Zakup",
    short: "Opłacone zamówienia tego produktu (EasyCart).",
    long:
      "Liczba z EasyCart dla tego produktu, nie z naszego licznika. " +
      "To prawda o sprzedaży dla tej kolumny.",
  },
  funnel_entry: {
    label: "Wejście",
    short: "Start wizyty na stronie.",
    long: "Ile wizyt zaczęło się w tej grupie (np. z danego źródła ruchu).",
  },
  funnel_content: {
    label: "Treść",
    short: "Przewinęli / zobaczyli treść.",
    long: "Ile wizyt zaangażowało się w treść (np. scroll, sekcje), zanim kliknęły dalej.",
  },
  funnel_cta: {
    label: "Przycisk CTA",
    short: "Kliknięcie wezwania do działania.",
    long: "Ile wizyt kliknęło główny przycisk (CTA), np. „Kupuję” albo „Dowiedz się więcej”.",
  },
  funnel_checkout: {
    label: "Koszyk",
    short: "Dotarcie do koszyka.",
    long: "Ile wizyt otworzyło koszyk EasyCart.",
  },
  funnel_purchase_path: {
    label: "Zakup",
    short: "Zakup powiązany z wizytą.",
    long: "Zakup, który nasz licznik połączył z tą wizytą. Pełną sprzedaż bierzesz z EasyCart.",
  },
  small_sample: {
    label: "Mała próbka",
    short: "Za mało wizyt, by ufać procentom.",
    long:
      "Poniżej 15 wizyt procenty potrafią skakać o kilkadziesiąt punktów przez jedną osobę. " +
      "Traktuj jako ciekawostkę, nie twardy wniosek.",
  },
  product_visits: {
    label: "Wizyty z tym produktem",
    short: "Największa z liczb kroków lejka.",
    long:
      "Przybliżona liczba wizyt związanych z tym produktem (największa z: widok, klik, koszyk, zakup). " +
      "Nie jest to „Sesje zakupowe” z EasyCart.",
  },
  landings_to_cart: {
    label: "Z których stron do koszyka",
    short: "Landing → koszyk tego produktu.",
    long:
      "Ile wizyt było na danej stronie i ile z nich otworzyło ten koszyk. " +
      "Strona główna może pojawiać się przy kilku produktach (te same wizyty).",
  },
  cvr_to_cart: {
    label: "Do koszyka (%)",
    short: "Udział wizyt, które otworzyły koszyk.",
    long: "Jaki procent wizyt w grupie otworzył koszyk (w żargonie: CVR checkout).",
  },
  cvr_to_purchase: {
    label: "Do zakupu (%)",
    short: "Udział wizyt z zakupem na ścieżce.",
    long: "Jaki procent wizyt zakończył się zakupem widocznym na ścieżce (w żargonie: CVR purchase).",
  },
  median_scroll: {
    label: "Ile przewijają (mediana)",
    short: "Środkowa głębokość przewinięcia.",
    long: "Połowa wizyt przewinęła mniej, połowa więcej. Pokazuje, czy ludzie czytają, czy tylko zerkną.",
  },
  abandoned_carts: {
    label: "Porzucone koszyki",
    short: "Koszyk bez zakupu na ścieżce.",
    long:
      "Weszli do koszyka, a zakup nie został połączony z ich wizytą. " +
      "Część mogła kupić bez śladu (patrz zakupy EasyCart vs ścieżka).",
  },
  cart_to_purchase: {
    label: "Koszyk → zakup (%)",
    short: "Zakup na ścieżce ÷ wejścia do koszyka.",
    long: "Jaki procent wejść do koszyka kończy się zakupem widocznym na ścieżce.",
  },
  visits: {
    label: "Wizyty",
    short: "Unikalne wizyty w okresie.",
    long: "Liczba wizyt (osobnych „wejść” na stronę) w wybranym okresie, według naszego licznika.",
  },
  path_order: {
    label: "Ścieżka stron",
    short: "Kolejność stron w jednej wizycie.",
    long: "Kolejność stron, które ktoś odwiedził w jednej wizycie, zanim wszedł do koszyka lub wyszedł.",
  },
  section_reach: {
    label: "Zasięg sekcji",
    short: "Ile wizyt zobaczyło tę sekcję.",
    long: "Procent wizyt na tej stronie, które dojechały wzrokiem / scrolliem do tej sekcji.",
  },
  section_cta: {
    label: "Kliknięcia CTA z sekcji",
    short: "Ile razy kliknięto przycisk na tym etapie.",
    long:
      "Liczba kliknięć CTA atrybuowanych do tej sekcji (przycisk w sekcji albo ostatnia widziana sekcja). " +
      "Pokazuje, na którym etapie ludzie najczęściej idą dalej. Pod spodem: etykiety przycisków.",
  },
  section_buyers: {
    label: "Sekcja wśród kupujących",
    short: "Udział wizyt z zakupem, które widziały sekcję.",
    long: "Zielony pasek: jak często tę sekcję widziały wizyty, które skończyły się zakupem.",
  },
  impulse: {
    label: "Impuls z social / ads",
    short: "Szybki zakup po wejściu z social lub reklamy.",
    long:
      "Zakup wkrótce po wejściu z Instagrama, Facebooka albo płatnej reklamy, " +
      "bez długiego czytania. Pokazuje, czy treść „sprzedaje od razu”.",
  },
  ga4_vs_real: {
    label: "Różnica Google vs EasyCart",
    short: "Google często widzi mniej lub inaczej.",
    long:
      "Google Analytics może zgłaszać inną liczbę zakupów niż EasyCart " +
      "(blokady reklam, zgody cookies, brak pełnej ścieżki). " +
      "Prawda o kasie: EasyCart. Google tylko do porównania.",
  },
  via_filter: {
    label: "Z którego linka",
    short: "Parametr ?via= w adresie.",
    long:
      "Nasze oznaczenie linku (np. bio, ManyChat). " +
      "Działa tylko gdy w adresie jest ?via=…. Bez tego wizyta trafia do „(brak)”.",
  },
  yt_funnel_overview: {
    label: "Lejek YouTube",
    short: "Rolka → film YT → landing → zakup.",
    long:
      "Mierzymy jednostkę treści (jeden film = jeden via=yt_*), nie tę samą osobę między Instagramem a YouTube. " +
      "Kliknięcia /y/ to social→YT. Sesje i zakupy to landing z /v/ (via + UTM youtube/video).",
  },
  yt_funnel_clicks: {
    label: "Kliknięcia social → YT",
    short: "Wejścia przez /y/{id} (suma z IG i FB).",
    long:
      "Ile razy ktoś kliknął shortlink do filmu na YouTube. " +
      "Łącznie = wszystkie from. IG (w tym ManyChat DM z ?from=ig) i FB (?from=fb) widać osobno. " +
      "To nie jest UTM na landingu.",
  },
  yt_funnel_sessions: {
    label: "Sesje z YT",
    short: "Wizyty landingu z via=yt_*.",
    long:
      "Sesje first-party journey po wejściu z /v/{id} (opis, end screen, pinned comment). " +
      "To nie jest liczba wyświetleń filmu w YouTube Studio.",
  },
  traffic_filter: {
    label: "Kanał (jak w GA4)",
    short: "Social organiczny, social płatny, bezpośredni itd.",
    long:
      "Grupy kanałów zbliżone do raportów Google Analytics 4. " +
      "Liczone po UTM i referrerze (np. instagram.com bez UTM = social organiczny). " +
      "To nadal wizyty na landingu, nie „nowi użytkownicy” z GA.",
  },
  visitor_filter: {
    label: "Nowi vs powracający",
    short: "Pierwsza wizyta czy kolejna.",
    long: "Czy to ktoś nowy na stronie, czy wraca (cookie naszego licznika).",
  },
  version_filter: {
    label: "Wersja strony (test)",
    short: "Którą wersję landingu zobaczyli.",
    long: "Wersja eksperymentu / treści na landingu. Służy do porównań A/B, nie do sprzedaży 1:1.",
  },
  kpi_today: {
    label: "Przychód dziś",
    short: "Suma opłaconych zamówień dziś (czas PL).",
    long:
      "Suma opłaconych zamówień EasyCart od północy czasu polskiego. Anulowane poza sumą.",
  },
  kpi_period: {
    label: "Przychód w okresie",
    short: "Suma opłaconych zamówień EasyCart.",
    long:
      "Suma opłaconych zamówień EasyCart w okresie. Dzień = kalendarz polski, jak w panelu EasyCart.",
  },
  easycart_shopping_sessions_note: {
    label: "Sesje zakupowe EasyCart",
    short: "Liczba z panelu EasyCart, której nie mamy w API.",
    long:
      "Panel EasyCart pokazuje własne „Sesje zakupowe”, których nie da się pobrać przez API. " +
      "Dlatego u nas tej liczby nie ma i nie porównuj jej 1:1 z naszymi wejściami do koszyka.",
  },
  biggest_drop: {
    label: "Największa ucieczka",
    short: "Krok, na którym odpada najwięcej osób.",
    long:
      "Między którymi krokami lejka znika najwięcej wizyt (w żargonie: drop). " +
      "Tam zwykle warto poprawić treść albo przycisk.",
  },
  daily_chart: {
    label: "Wykres dzienny",
    short: "Zmiany dzień po dniu.",
    long:
      "Każdy punkt / słupek to jeden dzień kalendarzowy (czas polski). " +
      "Gdy serie mają różną skalę (np. wizyty vs zakupy), wykres używa osobnych osi Y. " +
      "W tooltipie zawsze widzisz rzeczywiste liczby, nie procenty.",
  },
  hourly_chart: {
    label: "Wykres godzinowy",
    short: "Rozkład godzinowy (czas PL).",
    long:
      "Każdy punkt to godzina kalendarzowa Europe/Warsaw. " +
      "Gdy serie mają inną skalę, linia idzie w % własnego maksimum w oknie, a tooltip pokazuje rzeczywistą liczbę. " +
      "Na „Ruch i sprzedaż” tooltip dodaje top ?via= z sesji tej godziny (co przyprowadziło wejścia) " +
      "oraz publikacje z tej godziny. Wejścia bez parametru via trafiają do (brak). " +
      "Wykresy słupkowe z zł zostają na osiach bezwzględnych.",
  },
  reel_reach: {
    label: "Zasięg publikacji",
    short: "KPI: aktualny stan views. Wykres: przyrost między odczytami.",
    long:
      "W tabeli i KPI widać aktualną liczbę wyświetleń: rolki z dumpów Graph, karuzele z insights (wyświetlenia). " +
      "Przyrost jest na osobnym wykresie powierzchniowym: wysokość = suma IG+FB+karuzele, kolory = składowe " +
      "(godzina odczytu, czas PL). Pierwszy snapshot jest baseline i nie tworzy słupka. " +
      "Instagram i Facebook: Graph. Karuzele: insights. TikTok nie jest pobierany.",
  },
  manychat_keywords: {
    label: "Hasła ManyChat",
    short: "Krótkie komentarze: mama, matka, pakiet, ulga.",
    long:
      "To nie jest live API ManyChat. Liczymy krótkie komentarze (do 36 znaków, 1–3 wyrazy z listy) " +
      "i wrzucamy je na godzinę komentarza (czas PL).",
  },
  content_purchase_hard: {
    label: "Powiązane (twarde)",
    short: "Zakup z ?via= mapowanym na treść lub źródło.",
    long:
      "Sesja journey miała parametr ?via= (karuzela /k/, YT yt_*, bio, ManyChat itd.) i skończyła się zakupem. " +
      "To jedyne twarde powiązanie zamówienia z treścią lub oznaczonym kanałem.",
  },
  content_purchase_proximate: {
    label: "Blisko w czasie",
    short: "Hipoteza: publikacja w okolicy zakupu.",
    long:
      "Zamówienie EasyCart i publikacja (lub spike triggerów) w ±2h / tym samym dniu PL. " +
      "To korelacja, nie atrybucja. Nie mówimy, że ta rolka sprzedała zamówienie.",
  },
  content_purchase_unlinked: {
    label: "Bez powiązania",
    short: "Paid EasyCart bez via treści i bez sąsiedztwa.",
    long:
      "Zakup jest w kasie, ale nie mamy ?via= treści ani publikacji w oknie. " +
      "Pokazuje sufit pomiaru: ile sprzedaży content jeszcze nie łapie.",
  },
  funnel_views: {
    label: "Wyświetlenia (przyrost)",
    short: "Suma Δ views IG+FB+karuzele w okresie.",
    long:
      "Przyrost między snapshotami Graph w tygodniu/miesiącu (czas PL). " +
      "Pierwszy snapshot sztuki to baseline bez delty. To nie jest aktualny stan dumpów.",
  },
  funnel_entries: {
    label: "Wejścia",
    short: "Unikalne sesje journey w okresie.",
    long:
      "Sesje first-party z beaconu na landingach (journey.db), bez sesji testowych. " +
      "Inna populacja niż viewers social.",
  },
  funnel_carts: {
    label: "Koszyki",
    short: "Unikalne sesje z checkout_view.",
    long: "Ile różnych sesji otworzyło koszyk EasyCart w okresie (jedna sesja = max 1).",
  },
  funnel_purchases: {
    label: "Zakupy",
    short: "Opłacone zamówienia EasyCart.",
    long: "SSOT kasy: revenue_amount > 0, bez anulowanych/wykluczonych statusów KPI.",
  },
  funnel_wow: {
    label: "Zmiana tydzień do tygodnia",
    short: "Δ% vs poprzedni tydzień ISO (czas PL).",
    long:
      "Porównanie wybranego tygodnia ISO z poprzednim pełnym tygodniem w zakresie. " +
      "n/d gdy poprzedni okres miał zero (nie da się policzyć procentu).",
  },
  funnel_mom: {
    label: "Zmiana miesiąc do miesiąca",
    short: "Δ% vs poprzedni miesiąc kalendarzowy (czas PL).",
    long: "Jak WoW, ale dla miesięcy kalendarzowych Europe/Warsaw.",
  },
  content_top_hard: {
    label: "Przypisane via",
    short: "Zakup z ?via= mapowanym na treść.",
    long:
      "Najwyższa pewność: sesja z via (karuzela /k/, yt_*, kz_*) i opłacone zamówienie. " +
      "To jedyna sensowna atrybucja sztuki do sprzedaży.",
  },
  content_top_proximate: {
    label: "Blisko w czasie",
    short: "Publikacja ±2h / ten sam dzień co zakup.",
    long: "Korelacja czasowa, nie dowód. Używamy gdy brak twardego via.",
  },
  content_top_corr: {
    label: "Korelacja ruchu",
    short: "Duży przyrost views w godzinach ze skokiem zakupów.",
    long:
      "Najniższa pewność: materiał zyskał views w godzinach z powyżej-medianą zamówień EC. " +
      "Hipoteza do sprawdzenia, nie ranking do skalowania budżetu.",
  },
  intel_score: {
    label: "Score publikacji",
    short: "Proxy intencji: trigery, komentarze, views, likes + klasa życia.",
    long:
      "Wynik łączny: trigery×12 + min(komentarze,500)×0.15 + min(views,200k)/5000 + min(likes,5k)×0.02. " +
      "Bonus +2 gdy treść gotowa, +3 evergreen, +1 flash. To NIE jest atrybucja sprzedaży do rolki.",
  },
  intel_triggers: {
    label: "Trigery ManyChat",
    short: "Hasła intencji w komentarzach (np. mama, pakiet).",
    long:
      "Liczone z komentarzy pod publikacją (słowa jak w banku ManyChat). " +
      "Leading indicator, nie live bot i nie dowód zakupu.",
  },
  intel_life_flash: {
    label: "Flash",
    short: "Szybki peak w pierwszych dniach.",
    long:
      "Większość zasięgu (ok. ≥70% do D30) wpada w pierwsze 2 dni. Dobry na szybki test, słaby na late boost.",
  },
  intel_life_medium: {
    label: "Medium",
    short: "Życie pomiędzy flash a evergreen.",
    long: "Ani błyskawiczny peak, ani wyraźny długi ogon. Traktuj indywidualnie względem Top score.",
  },
  intel_life_evergreen: {
    label: "Evergreen",
    short: "Długi ogon: warto wracać do treści.",
    long:
      "Mały udział pierwszych 2 dni albo wyraźny przyrost po D7. Kandydat do stories, ads i republish.",
  },
  acquisition_channels: {
    label: "Kanały (jak w GA4)",
    short: "Podział wizyt według kanału akwizycji.",
    long:
      "Kanały liczymy po UTM i referrerze, nazwy jak w Google Analytics 4 " +
      "(social organiczny, social płatny, bezpośredni itd.). " +
      "Wiersz „Nowe wizyty” odpowiada najbliżej karty GA „Nowi użytkownicy”, ale to nadal nasz licznik sesji, " +
      "nie kopia GA. Szczegóły masz w filtrze „Z którego linka” (?via=) i surowych UTM.",
  },
  paid_entries: {
    label: "Płatne wejścia",
    short: "Wizyty z reklamy (?via=ad lub Social płatny).",
    long:
      "Sesje oznaczone linkiem ?via=ad albo sklasyfikowane jako Social płatny (UTM płatny, referrer). " +
      "Reszta wizyt trafia do „Pozostałe”.",
  },
  other_entries: {
    label: "Pozostałe wejścia",
    short: "Wizyty spoza płatnych kampanii.",
    long: "Sesje bez ?via=ad i bez kanału Social płatny: organiczny social, direct, wyszukiwarka itd.",
  },
  shopping_session_list: {
    label: "Lista sesji z koszykiem",
    short: "Pojedyncze wizyty z koszykiem (zakup lub porzucenie).",
    long:
      "Każdy wiersz to jedna wizyta, która dotarła do koszyka. Kliknij wiersz po pełny przebieg. " +
      "Identyfikator sesji jest skrócony. Bez danych osobowych.",
  },
  session_timeline: {
    label: "Timeline zdarzeń",
    short: "Kolejność działań w jednej wizycie.",
    long:
      "Chronologiczna lista zdarzeń z naszego licznika: strony, sekcje, CTA, koszyk, zakup. " +
      "Przy słabej atrybucji timeline może być krótki mimo opłaconego zamówienia.",
  },
  comments_total: {
    label: "Komentarze w filtrze",
    short: "Ile wpisów przechodzi przez wybrane filtry.",
    long:
      "Liczymy z plików kanałów (IG, FB, YT, TT). Filtr „bez replies” zostawia tylko nowy głos, nie debatę pod komentarzem.",
  },
  comments_7d: {
    label: "Komentarze 7 dni",
    short: "Wpisy z datą z ostatnich 7 dni (czas PL).",
    long:
      "TikTok często ma datę zebrania, nie publikacji. IG i FB mają timestamp z Graph.",
  },
  comments_cta: {
    label: "CTA Mama",
    short: "Krótkie hasła mama/matka.",
    long:
      "To samo co hasła ManyChat na Rolki vs sprzedaż: do 36 znaków, 1–3 wyrazy z listy. Nie jest to live API ManyChat.",
  },
  comments_historia: {
    label: "Historie",
    short: "Komentarze-sceny pierwszej osoby.",
    long:
      "Klasa z analiz 2026-08: ktoś opowiada swoje życie, nie tylko stawia emoji albo hasło.",
  },
  comments_pytanie: {
    label: "Pytania",
    short: "Komentarze zaklasyfikowane jako pytanie.",
    long: "Pytają o radę, produkt albo „co zrobić”. Dobre do kolejki dla Kasi albo do karuzeli typu pytanie.",
  },
  comments_action: {
    label: "Do akcji",
    short: "Wysoki score biznesowy.",
    long:
      "Score z Comment Intelligence: ból, pytanie, intencja zakupu, świeżość. Próg jak w digestach (średni i wyższy).",
  },
  session_attribution: {
    label: "Jakość atrybucji",
    short: "Jak dobrze połączyliśmy zakup ze ścieżką na landingu.",
    long:
      "Pełna ścieżka = widzimy landing przed koszykiem. Syntetyczny identyfikator = zakup z EasyCart " +
      "bez pełnej historii strony (np. wejście prosto w sklep).",
  },
  pub_section_reach: {
    label: "Zasięg Meta",
    short: "Osobno Instagram i Facebook z Graph.",
    long:
      "Liczby z Meta Graph Insights. IG i FB są osobno: nie sumujemy ich w jednej komórce Reach. " +
      "Gdy kanału nie było w publikacji, wiersz mówi „brak publikacji”, a nie udaje zera sukcesu.",
  },
  pub_section_funnel: {
    label: "Nasz lejek",
    short: "Wejścia / koszyki / zakupy po linku via tej publikacji.",
    long:
      "First-party z journey.db po parametrze ?via= (krótki link /k/…). " +
      "Jeden via na publikację, więc IG i FB dzielą ten sam lejek klików.",
  },
  pub_ig_reach: {
    label: "IG Reach",
    short: "Unikalni odbiorcy na Instagramie.",
    long: "Reach z insights media IG (Graph). Aktualny stan, nie przyrost z wykresu.",
  },
  pub_ig_saved: {
    label: "IG Save",
    short: "Zapisania posta na Instagramie.",
    long: "Ile osób zapisało karuzelę / post na IG. Silny sygnał „chcę wrócić”.",
  },
  pub_ig_shares: {
    label: "IG Share",
    short: "Udostępnienia na Instagramie.",
    long: "Udostępnienia z insights IG. Wysoki share przy niskim kliku via = treść krąży, link słabo izolowany.",
  },
  pub_ig_comments: {
    label: "IG Komentarze",
    short: "Komentarze pod postem IG.",
    long: "Liczba komentarzy z Graph (albo policzonych z listy), osobno od FB.",
  },
  pub_ig_save_rate: {
    label: "IG Save %",
    short: "Zapisania ÷ reach IG.",
    long: "Udział osób z zasięgu IG, które zapisały. Powyżej ~3% to mocny sygnał w ocenie „mocne”.",
  },
  pub_fb_reach: {
    label: "FB Reach",
    short: "Unikalni odbiorcy na Facebooku.",
    long: "post_impressions_unique z insights posta na Page. Osobno od IG.",
  },
  pub_fb_impressions: {
    label: "FB Imp.",
    short: "Wyświetlenia posta na Facebooku.",
    long: "post_impressions z Graph Page. FB nie daje tu Save/Share jak IG w tym hubie.",
  },
  pub_fb_comments: {
    label: "FB Komentarze",
    short: "Komentarze pod postem FB.",
    long: "Policzone z Graph comments na post Page, osobno od IG.",
  },
  pub_delta_1d: {
    label: "Δ 1d",
    short: "Przyrost views ~ostatnia doba.",
    long:
      "Różnica między ostatnim a wcześniejszym snapshotem wyświetleń karuzeli (~okno 28 h). " +
      "Jedna historia na publikację (primary views), nie osobny słupek IG i FB.",
  },
  pub_sessions: {
    label: "Sesje via",
    short: "Wejścia first-party z via tej publikacji.",
    long:
      "Liczba sesji w journey.db z parametrem via / short linkiem materiału. " +
      "To nie są „kliki” Meta Ads. Kolumna clicks_via w DB trzyma tę samą liczbę (alias historyczny).",
  },
  pub_checkouts: {
    label: "Koszyki",
    short: "Sesje via, które otworzyły koszyk.",
    long: "Wejścia z via tej publikacji z has_checkout = 1 w naszym liczniku.",
  },
  pub_purchases: {
    label: "Zakupy",
    short: "Sesje via z zakupem na ścieżce.",
    long:
      "Wizyty z via tej publikacji oznaczone has_purchase. Pełną kasę EasyCart bierz z zakładki Sprzedaż.",
  },
  pub_cvr_checkout: {
    label: "CVR koszyk",
    short: "Koszyki ÷ sesje via.",
    long: "Jaki procent wejść z via tej publikacji otworzył koszyk.",
  },
  pub_cvr_purchase: {
    label: "CVR zakup",
    short: "Zakupy ÷ sesje via.",
    long: "Jaki procent wejść z via tej publikacji skończył się zakupem na ścieżce.",
  },
  pub_score: {
    label: "Score",
    short: "Ocena 0–1 z save/share/komentarzy/sesji via/zakupów.",
    long:
      "Ważona mieszanka: save/reach, share/reach, jakość komentarzy, sesje via (journey), zakupy. " +
      "Liczone na agregacie (IG preferowane, gdy jest). Nie mylić ze scorecardem house w studiu.",
  },
  pub_via: {
    label: "Via / short link",
    short: "Oznaczenie linku tej publikacji.",
    long:
      "Parametr ?via= i krótki URL /k/…. Po nim łączymy sesje journey i koszyki z tą konkretną karuzelą / postem.",
  },
  pub_kpi_publications: {
    label: "Publikacje",
    short: "Ile pozycji ze studia w oknie.",
    long: "Liczba opublikowanych karuzel / postów / czasotekstów ze studia w wybranym oknie dni.",
  },
  pub_kpi_reach: {
    label: "Zasięg IG / FB",
    short: "Suma reach osobno per kanał.",
    long:
      "Reach IG i Reach FB sumowane osobno po kartach. Nie traktuj jednej liczby „Reach” jako sumy obu kanałów bez sprawdzenia podpisu.",
  },
  pub_kpi_funnel: {
    label: "Nasz lejek (KPI)",
    short: "Suma sesji via w oknie.",
    long: "Suma wejść first-party z via wszystkich publikacji w oknie; pod spodem koszyki i zakupy.",
  },
  pub_kpi_cvr: {
    label: "CVR / score",
    short: "CVR zakup w oknie + mediana score.",
    long: "Zakupy ÷ sesje w całym oknie. Mediana score to środek rozkładu ocen pojedynczych publikacji.",
  },
  funnel_tofu: {
    label: "TOFU",
    short: "Góra lejka: zasięg / rozpoznawalność.",
    long: "Treść na wejście: ból, rozpoznanie, zasięg. Target mixu tygodnia zwykle ~40%.",
  },
  funnel_mofu: {
    label: "MOFU",
    short: "Środek lejka: edukacja / zaufanie.",
    long: "Treść edukacyjna, narzędzia, pogłębienie. Target mixu zwykle ~40%.",
  },
  funnel_bofu: {
    label: "BOFU",
    short: "Dół lejka: decyzja / oferta.",
    long: "Treść bliżej zakupu: oferta, obiekcje, CTA. Target mixu zwykle ~20%.",
  },
};

export function getMetricTip(id: MetricTipId): MetricTip {
  return METRICS_GLOSSARY[id];
}

export const PROFILE_LABELS_PL: Record<string, string> = {
  impulse_buy: "Kupił od razu",
  read_and_buy: "Poczytał i kupił",
  checkout_abandon: "Porzucił koszyk",
  deep_no_buy: "Czytał długo, nie kupił",
  shallow_no_buy: "Rzucił okiem i wyszedł",
  accidental_exit: "Wyszedł przypadkiem",
  explore_no_buy: "Poklikał, nie kupił",
};

export function profileLabelPl(key: string): string {
  return PROFILE_LABELS_PL[key] || key;
}
